import { useMemo, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { EmptyState } from "../primitives";
import { Panel, PanelRow } from "./Panel";
import { useListChildren, useEntityTypes, useTableNodes } from "../../../lib/hooks/use-kuvio";
import { NewTableModal } from "../table/NewTableModal";
import type { LibraryNode } from "../../../lib/types";

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface OwnedTablesGridProps {
  nodeId: string;
}

interface OwnedTableRow {
  key: string;
  name: string;
  tableNodeId: string | undefined;
  count: number;
  updatedAt: string | undefined;
}

/**
 * Sprint 9: an agent's Tables panel reads by ownership (`owner_agent_id` on the
 * entity type), NOT by tree containment. Tables live at workspace root; an agent
 * "owns" one via metadata. Backward compat: when nothing is owned but tables are
 * still nested under this node (legacy workspaces), we surface them under a
 * "Nested tables" subhead so they don't vanish.
 */
export function OwnedTablesGrid({ nodeId }: OwnedTablesGridProps) {
  const entityTypes = useEntityTypes();
  const tableNodes = useTableNodes();
  const children = useListChildren(nodeId);
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  // entity_type_slug → table node (for last-modified + a navigation fallback).
  const nodeBySlug = useMemo(() => {
    const map = new Map<string, LibraryNode>();
    for (const n of tableNodes.data ?? []) {
      if (n.entity_type_slug) map.set(n.entity_type_slug, n);
    }
    return map;
  }, [tableNodes.data]);

  // Tables this agent OWNS (metadata) — the Sprint 9 primary source.
  const owned = useMemo<OwnedTableRow[]>(() => {
    return (entityTypes.data ?? [])
      .filter((t) => t.owner_agent_id === nodeId)
      .map((t) => {
        const node = t.slug ? nodeBySlug.get(t.slug) : undefined;
        return {
          key: t.slug,
          name: t.name,
          tableNodeId: t._table_node_id ?? node?.id,
          count: t._record_count ?? 0,
          updatedAt: node?.updated_at,
        };
      });
  }, [entityTypes.data, nodeBySlug, nodeId]);

  // Legacy: tables parented under this node. Excluded if already shown as owned.
  const ownedNodeIds = new Set(owned.map((o) => o.tableNodeId).filter(Boolean));
  const nested = (children.data ?? []).filter(
    (c) => c.type === "table" && !ownedNodeIds.has(c.id),
  );

  const total = owned.length + nested.length;
  const isLoading = entityTypes.isLoading || children.isLoading;

  const newTableButton = (
    <button onClick={() => setModalOpen(true)} style={newButtonStyle}>
      <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" />
      New table
    </button>
  );

  function navigateOwned(row: OwnedTableRow) {
    if (row.tableNodeId) navigate(`/agents/${nodeId}/tables/${row.tableNodeId}`);
  }

  return (
    <>
      <Panel
        title="Tables"
        count={total}
        action={total > 0 ? newTableButton : undefined}
        isLoading={isLoading}
        error={entityTypes.error ?? children.error}
        onRetry={() => {
          entityTypes.refetch();
          children.refetch();
        }}
        isEmpty={total === 0}
        empty={
          <EmptyState>
            <div>No tables yet.</div>
            <button onClick={() => setModalOpen(true)} style={{ ...newButtonStyle, margin: "10px auto 0" }}>
              <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" />
              New table
            </button>
          </EmptyState>
        }
      >
        {owned.map((row) => (
          <PanelRow
            key={row.key}
            onClick={() => navigateOwned(row)}
            title={row.name}
            trailing={
              <span style={metaStyle}>
                {row.count} {row.count === 1 ? "row" : "rows"}
                {row.updatedAt ? ` · ${formatRelativeTime(row.updatedAt)}` : ""}
              </span>
            }
          >
            <i className="ti ti-table" style={{ fontSize: 16, color: "var(--color-text-tertiary)", flexShrink: 0 }} aria-hidden="true" />
            <span style={nameStyle}>{row.name}</span>
          </PanelRow>
        ))}

        {nested.length > 0 && (
          <>
            {owned.length > 0 && <div style={subheadStyle}>Nested tables</div>}
            {nested.map((table) => (
              <PanelRow
                key={table.id}
                onClick={() => navigate(`/agents/${nodeId}/tables/${table.id}`)}
                title={table.name}
                trailing={
                  <span style={metaStyle}>
                    {table._child_count} {table._child_count === 1 ? "row" : "rows"} · {formatRelativeTime(table.updated_at)}
                  </span>
                }
              >
                <i className="ti ti-table" style={{ fontSize: 16, color: "var(--color-text-tertiary)", flexShrink: 0 }} aria-hidden="true" />
                <span style={nameStyle}>{table.name}</span>
              </PanelRow>
            ))}
          </>
        )}
      </Panel>

      <NewTableModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        ownerAgentId={nodeId}
        onCreated={(tableNodeId) => navigate(`/agents/${nodeId}/tables/${tableNodeId}`)}
      />
    </>
  );
}

const newButtonStyle: CSSProperties = {
  fontSize: 12,
  padding: "4px 10px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
};

const nameStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const metaStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
};

const subheadStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-tertiary)",
  padding: "8px 0 4px",
};
